import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AccountCircle,
  Add,
  Assignment,
  AssignmentTurnedIn,
  BatteryFull,
  CalendarMonth,
  CheckCircle,
  ChevronRight,
  Close,
  Edit,
  ErrorOutline,
  FilterList,
  Folder,
  Groups,
  Home,
  Key,
  LocationOn,
  Lock,
  Logout,
  NotificationsNone,
  Payments,
  Person,
  Save,
  Search,
  Shield,
  SignalCellularAlt,
  Storage,
  WarningAmber,
  Wifi,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import {
  changePassword,
  createActivityCatalog,
  createClient,
  createDailyClosure,
  createInvestment,
  createRecovery,
  fetchActivityCatalog,
  fetchClientFiche,
  fetchClients,
  fetchDailyClosureDetails,
  fetchDailyClosures,
  fetchIncidents,
  fetchInvestmentSchedule,
  fetchInvestments,
  fetchNotifications,
  fetchProducts,
  fetchRecoveries,
  getDashboardSummary,
  markAllNotificationsRead,
  markNotificationRead,
  updateClient,
  updateInvestment,
} from "../services/owdedApi";
import { currentMonthRange } from "./listUtils";
import { dateOnly, money, number } from "./format";
import { canOperateRecovery, displayModeKey, hasFullAccess } from "../utils/access";
import { LoadingOverlay } from "./ui";
import "./MobileWorkspace.scss";

const fallbackSummary = {
  portfolio: { total_clients: 0, new_clients: 0 },
  investments: { total_investments: 0, investments_pending: 0, by_currency: {} },
  recoveries: { by_currency: {} },
  risk: { late_clients: 0, by_currency: {} },
};

const clientInitialForm = {
  last_name: "",
  first_name: "",
  sex: "M",
  phone: "",
  address: "",
  commune: "",
  activity_ids: [],
  photo_client: null,
  business_photo: null,
  identity_card: null,
  estimated_daily_revenue: 0,
  estimated_monthly_revenue: 0,
  estimated_monthly_charges: 0,
  existing_working_capital: 0,
  risk_score: 0,
};

const investmentInitialForm = {
  client_id: "",
  product_id: "",
  subscription_amount: 300000,
  repayment_start_date: "",
  analysis_notes: "",
};

const paymentInitialForm = {
  investment_id: "",
  amount_paid: 0,
  latitude: "",
  longitude: "",
  observation: "",
};

const passwordInitialForm = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const pageSize = 5;
const editableInvestmentStatuses = ["DEMANDE", "ANALYSE", "VALIDATION"];

function currencyStack(source, field) {
  return (
    <span className="mobile-amount-stack">
      {["CDF", "USD"].map((currency) => <span key={currency}>{money(source?.[currency]?.[field], currency)}</span>)}
    </span>
  );
}

function statusClass(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized.includes("RETARD") || normalized.includes("OPEN") || normalized.includes("REJETE")) return "danger";
  if (normalized.includes("PARTIEL") || normalized.includes("VALID") || normalized.includes("DEMANDE") || normalized.includes("ANALYSE") || normalized.includes("DECAISSE")) return "warning";
  if (normalized.includes("SOLDE") || normalized.includes("CLOTURE") || normalized.includes("CLOS") || normalized.includes("PAID")) return "success";
  return "info";
}

function normalizeRole(user) {
  return String(user?.role_code || "").toUpperCase();
}

function canManageInvestments(user) {
  const role = normalizeRole(user);
  return hasFullAccess(user) || ["AGENT_COMMERCIAL", "AGENT_CREDIT"].includes(role);
}

function paginate(rows, page) {
  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  return {
    totalPages,
    rows: rows.slice((safePage - 1) * pageSize, safePage * pageSize),
    page: safePage,
  };
}

function compactDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function safeCurrency(item) {
  return item?.currency || item?.product_currency || "CDF";
}

function contributionInstallments(product) {
  if (!product) return 1;
  const duration = Number(product.duration_value || 1);
  const frequency = product.contribution_frequency || product.repayment_modality || "DAILY";
  if (frequency === "MONTHLY") return Math.max(duration, 1);
  if (frequency === "WEEKLY") {
    const totalDays = product.duration_unit === "MONTH" ? duration * 30 : duration;
    return Math.max(Math.ceil(totalDays / 7), 1);
  }
  if (product.duration_unit === "MONTH") return Math.max(duration * 30, 1);
  return Math.max(duration, 1);
}

function isBootstrapping(product) {
  return String(product?.product_family || product?.product_type_label || "").toUpperCase() === "BOOTSTRAPPING";
}

function investmentPreview(product, amount) {
  const gross = Number(amount || 0);
  if (!product) {
    return { commission: 0, net: gross, amountToRecover: gross, expectedContribution: 0, monthlyCommission: 0 };
  }

  const installments = contributionInstallments(product);
  const monthlyCommission = Number(((gross * Number(product.commission_rate || 0)) / 100).toFixed(2));
  const commission = isBootstrapping(product) ? Number((monthlyCommission * installments).toFixed(2)) : monthlyCommission;
  const amountToRecover = isBootstrapping(product) ? Number((gross + commission).toFixed(2)) : gross;
  const net = isBootstrapping(product) ? gross : Number((gross - commission).toFixed(2));
  const expectedContribution = Number((amountToRecover / Math.max(installments, 1)).toFixed(2));

  return { commission, net, amountToRecover, expectedContribution, monthlyCommission };
}

async function optimizeImage(file) {
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size <= 1024 * 1024) return file;

  const imageUrl = URL.createObjectURL(file);
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageUrl;
  });

  const maxSize = 1600;
  const ratio = Math.min(maxSize / image.width, maxSize / image.height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(image.width * ratio);
  canvas.height = Math.round(image.height * ratio);
  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
  URL.revokeObjectURL(imageUrl);

  if (!blob || blob.size >= file.size) return file;
  return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
}

function MobileWorkspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const monthRange = useMemo(() => currentMonthRange(), []);
  const [tab, setTab] = useState("home");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Traitement en cours...");
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState({ clients: 1, investments: 1, recovery: 1, notifications: 1 });
  const [summary, setSummary] = useState(fallbackSummary);
  const [clients, setClients] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [closures, setClosures] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [products, setProducts] = useState([]);
  const [activityCatalog, setActivityCatalog] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newActivity, setNewActivity] = useState("");
  const [clientForm, setClientForm] = useState(clientInitialForm);
  const [investmentForm, setInvestmentForm] = useState(investmentInitialForm);
  const [editingClient, setEditingClient] = useState(null);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [paymentForm, setPaymentForm] = useState(paymentInitialForm);
  const [passwordForm, setPasswordForm] = useState(passwordInitialForm);

  const withBusy = async (label, task) => {
    setBusyLabel(label);
    setBusy(true);
    setNotice("");
    try {
      return await task();
    } catch (err) {
      setNotice(err.response?.data?.error || err.message || "Operation impossible pour le moment.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    const params = { start_date: monthRange.start, end_date: monthRange.end };
    const [
      summaryData,
      clientRows,
      investmentRows,
      recoveryRows,
      incidentRows,
      closureRows,
      productRows,
      activities,
      notificationRows,
    ] = await Promise.all([
      getDashboardSummary(params).catch(() => fallbackSummary),
      fetchClients().catch(() => []),
      fetchInvestments().catch(() => []),
      fetchRecoveries(params).catch(() => []),
      fetchIncidents({ status: "OPEN" }).catch(() => []),
      fetchDailyClosures(params).catch(() => []),
      fetchProducts({ status: "ACTIVE" }).catch(() => []),
      fetchActivityCatalog().catch(() => []),
      fetchNotifications({ limit: 50 }).catch(() => []),
    ]);
    setSummary({ ...fallbackSummary, ...summaryData });
    setClients(clientRows || []);
    setInvestments(investmentRows || []);
    setRecoveries(recoveryRows || []);
    setIncidents(incidentRows || []);
    setClosures(closureRows || []);
    setProducts(productRows || []);
    setActivityCatalog(activities || []);
    setNotifications(notificationRows || []);
  }, [monthRange.end, monthRange.start]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setTab("home");
    setQuery("");
    setPages({ clients: 1, investments: 1, recovery: 1, notifications: 1 });
  }, [user?.id, user?.userId]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4500);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    setPages((current) => ({ ...current, [tab]: 1 }));
  }, [query, tab]);

  const currentUserId = Number(user?.id || user?.userId || 0);
  const role = normalizeRole(user);
  const userHasFullAccess = hasFullAccess(user);
  const canCreateOrEditInvestment = canManageInvestments(user);
  const canRecordRecovery = canOperateRecovery(user);
  const sameUser = useCallback((value) => Number(value || 0) === currentUserId, [currentUserId]);

  const directClientIds = useMemo(
    () => new Set(clients
      .filter((client) => sameUser(client.commercial_agent_id) || sameUser(client.created_by))
      .map((client) => Number(client.id))),
    [clients, sameUser]
  );

  const scopedInvestments = useMemo(
    () => investments.filter((item) => (
      userHasFullAccess
      || sameUser(item.agent_commercial_id)
      || sameUser(item.agent_recovery_id)
      || directClientIds.has(Number(item.client_id))
    )),
    [directClientIds, investments, sameUser, userHasFullAccess]
  );

  const scopedClientIds = useMemo(
    () => new Set([
      ...directClientIds,
      ...scopedInvestments.map((item) => Number(item.client_id)),
    ]),
    [directClientIds, scopedInvestments]
  );

  const scopedClients = useMemo(
    () => clients.filter((client) => userHasFullAccess || scopedClientIds.has(Number(client.id))),
    [clients, scopedClientIds, userHasFullAccess]
  );

  const scopedInvestmentIds = useMemo(() => new Set(scopedInvestments.map((item) => Number(item.id))), [scopedInvestments]);
  const scopedInvestmentRefs = useMemo(
    () => new Set(scopedInvestments.flatMap((item) => [item.reference, item.collection_card_id].filter(Boolean))),
    [scopedInvestments]
  );

  const scopedRecoveries = useMemo(
    () => recoveries.filter((item) => (
      userHasFullAccess
      || sameUser(item.agent_recovery_id)
      || scopedInvestmentIds.has(Number(item.investment_id))
    )),
    [recoveries, sameUser, scopedInvestmentIds, userHasFullAccess]
  );

  const scopedIncidents = useMemo(
    () => incidents.filter((item) => (
      userHasFullAccess
      || sameUser(item.assigned_to)
      || scopedInvestmentIds.has(Number(item.investment_id))
      || scopedInvestmentRefs.has(item.investment_reference)
    )),
    [incidents, sameUser, scopedInvestmentIds, scopedInvestmentRefs, userHasFullAccess]
  );

  const localSummary = useMemo(() => {
    const byCurrency = (rows, amountField) => rows.reduce((acc, row) => {
      const currency = String(row.currency || row.product_currency || "CDF").toUpperCase();
      acc[currency] = Number(acc[currency] || 0) + Number(row[amountField] || 0);
      return acc;
    }, { CDF: 0, USD: 0 });
    const recovered = byCurrency(scopedRecoveries, "amount_paid");
    const pending = scopedInvestments.filter((item) => ["DEMANDE", "ANALYSE", "VALIDATION"].includes(item.status)).length;
    const lateClientIds = new Set(scopedIncidents.map((item) => item.client_id || item.client_number || item.client_name));

    return {
      portfolio: {
        total_clients: scopedClients.length,
        new_clients: scopedClients.filter((client) => {
          const created = String(client.created_at || "").slice(0, 10);
          return created >= monthRange.start && created <= monthRange.end;
        }).length,
      },
      investments: {
        investments_pending: pending,
        total_investments: scopedInvestments.length,
      },
      recoveries: {
        by_currency: {
          CDF: { recovered_total: recovered.CDF, recovered_today: recovered.CDF },
          USD: { recovered_total: recovered.USD, recovered_today: recovered.USD },
        },
      },
      risk: {
        late_clients: lateClientIds.size,
      },
    };
  }, [monthRange.end, monthRange.start, scopedClients, scopedIncidents, scopedInvestments, scopedRecoveries]);

  const visibleSummary = userHasFullAccess ? summary : localSummary;
  const unreadCount = notifications.filter((item) => !item.read_at).length;

  const latestInvestmentByClient = useMemo(() => {
    const map = new Map();
    scopedInvestments.forEach((investment) => {
      const key = Number(investment.client_id);
      const current = map.get(key);
      const currentDate = new Date(current?.created_at || current?.investment_date || 0).getTime();
      const nextDate = new Date(investment.created_at || investment.investment_date || 0).getTime();
      if (!current || nextDate >= currentDate) map.set(key, investment);
    });
    return map;
  }, [scopedInvestments]);

  const filteredClients = useMemo(() => {
    const term = query.toLowerCase();
    return scopedClients.filter((client) => {
      const investment = latestInvestmentByClient.get(Number(client.id));
      const haystack = [
        client.generated_client_id,
        client.client_number,
        client.first_name,
        client.last_name,
        client.full_name,
        client.phone,
        client.activities,
        investment?.product_name,
        investment?.reference,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [latestInvestmentByClient, query, scopedClients]);

  const filteredInvestments = useMemo(() => {
    const term = query.toLowerCase();
    return scopedInvestments.filter((item) => [
      item.reference,
      item.collection_card_id,
      item.client_name,
      item.client_number,
      item.product_name,
      item.status,
    ].filter(Boolean).join(" ").toLowerCase().includes(term));
  }, [query, scopedInvestments]);

  const recoveryDossiers = useMemo(
    () => filteredInvestments.filter((item) => ["EN_RECOUVREMENT", "DECAISSE", "VALIDE"].includes(String(item.status || "").toUpperCase()) || Number(item.remaining_amount || 0) > 0),
    [filteredInvestments]
  );

  const clientPage = paginate(filteredClients, pages.clients || 1);
  const investmentPage = paginate(filteredInvestments, pages.investments || 1);
  const recoveryPage = paginate(recoveryDossiers, pages.recovery || 1);
  const notificationPage = paginate(notifications, pages.notifications || 1);

  const selectedProduct = products.find((product) => String(product.id) === String(investmentForm.product_id));
  const gross = Number(investmentForm.subscription_amount || 0);
  const minAmount = Number(selectedProduct?.min_amount || 0);
  const maxAmount = Number(selectedProduct?.max_amount || 0);
  const amountOutOfRange = selectedProduct && (gross < minAmount || gross > maxAmount);
  const preview = investmentPreview(selectedProduct, gross);
  const commission = preview.commission;
  const net = preview.net;
  const expectedContribution = preview.expectedContribution;
  const monthlyClientRevenue = Number((Number(clientForm.estimated_daily_revenue || 0) * 26).toFixed(2));

  const recoveryOptions = scopedInvestments.filter((item) => ["EN_RECOUVREMENT", "DECAISSE", "VALIDE"].includes(String(item.status || "").toUpperCase()));

  const navItems = [
    { key: "home", label: "Accueil", icon: <Home /> },
    { key: "clients", label: "Clients", icon: <Groups /> },
    { key: "investments", label: "Invest.", icon: <Assignment /> },
    { key: "recovery", label: "Recouvr.", icon: <Payments /> },
    { key: "account", label: "Compte", icon: <AccountCircle /> },
  ];

  const setActivePage = (key, value) => {
    setPages((current) => ({ ...current, [key]: value }));
  };

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      setPaymentForm((current) => ({
        ...current,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }));
    });
  };

  const toggleActivity = (id) => {
    const value = String(id);
    setClientForm((current) => ({
      ...current,
      activity_ids: current.activity_ids.includes(value)
        ? current.activity_ids.filter((item) => item !== value)
        : [...current.activity_ids, value],
    }));
  };

  const addActivity = async () => {
    const name = newActivity.trim();
    if (!name) return;
    await createActivityCatalog({ name });
    setNewActivity("");
    setActivityCatalog(await fetchActivityCatalog());
  };

  const toClientFormData = async () => {
    const data = new FormData();
    const fullName = `${clientForm.last_name || ""} ${clientForm.first_name || ""}`.trim();
    const selectedNames = activityCatalog
      .filter((activity) => clientForm.activity_ids.includes(String(activity.id)))
      .map((activity) => activity.name);

    Object.entries({
      last_name: clientForm.last_name || "",
      first_name: clientForm.first_name || "",
      full_name: fullName,
      sex: clientForm.sex || "M",
      phone: clientForm.phone || "",
      address: clientForm.address || "",
      commune: clientForm.commune || "",
      main_activity: selectedNames.join(", "),
      activities: JSON.stringify(clientForm.activity_ids),
      estimated_daily_revenue: clientForm.estimated_daily_revenue || 0,
      estimated_monthly_revenue: monthlyClientRevenue,
      estimated_monthly_charges: clientForm.estimated_monthly_charges || 0,
      existing_working_capital: clientForm.existing_working_capital || 0,
      risk_score: Math.min(Math.max(Number(clientForm.risk_score || 0), 0), 10),
    }).forEach(([key, value]) => data.append(key, value));

    if (clientForm.photo_client) data.append("photo_client", await optimizeImage(clientForm.photo_client));
    if (clientForm.business_photo) data.append("business_photo", await optimizeImage(clientForm.business_photo));
    if (clientForm.identity_card) data.append("identity_card", await optimizeImage(clientForm.identity_card));

    return data;
  };

  const clientActivityIds = (client) => {
    if (Array.isArray(client?.activity_ids)) return client.activity_ids.map(String);
    return String(client?.activity_ids || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const openClientForm = (client = null) => {
    setEditingClient(client);
    setClientForm(client ? {
      ...clientInitialForm,
      last_name: client.last_name || "",
      first_name: client.first_name || "",
      sex: client.sex || "M",
      phone: client.phone || "",
      address: client.address || "",
      commune: client.commune || "",
      activity_ids: clientActivityIds(client),
      estimated_daily_revenue: client.estimated_daily_revenue || 0,
      estimated_monthly_revenue: client.estimated_monthly_revenue || 0,
      estimated_monthly_charges: client.estimated_monthly_charges || 0,
      existing_working_capital: client.existing_working_capital || 0,
      risk_score: client.risk_score || 0,
    } : clientInitialForm);
    setModal("client");
  };

  const submitClient = async (event) => {
    event.preventDefault();
    await withBusy(editingClient ? "Modification du client..." : "Enregistrement du client...", async () => {
      if (editingClient) {
        await updateClient(editingClient.id, await toClientFormData());
      } else {
        await createClient(await toClientFormData());
      }
      setClientForm(clientInitialForm);
      setEditingClient(null);
      setModal(null);
      setNotice(editingClient ? "Client mis a jour." : "Client enregistre et rattache a votre compte.");
      await load();
    });
  };

  const openInvestmentForm = (investment = null) => {
    if (!canCreateOrEditInvestment) return;
    setEditingInvestment(investment);
    setInvestmentForm(investment ? {
      client_id: investment.client_id || "",
      product_id: investment.product_id || "",
      subscription_amount: investment.subscription_amount || investment.amount_invested || 0,
      repayment_start_date: investment.repayment_start_date ? String(investment.repayment_start_date).slice(0, 10) : "",
      analysis_notes: investment.analysis_notes || "",
    } : investmentInitialForm);
    setModal("investment");
  };

  const canEditInvestment = (investment) => canCreateOrEditInvestment
    && editableInvestmentStatuses.includes(String(investment?.status || "").toUpperCase());

  const submitInvestment = async (event) => {
    event.preventDefault();
    if (!canCreateOrEditInvestment) {
      setNotice("Votre profil ne peut pas creer un investissement.");
      return;
    }
    if (!selectedProduct) {
      setNotice("Veuillez choisir un produit.");
      return;
    }
    if (amountOutOfRange) {
      setNotice(`Le montant doit etre compris entre ${money(minAmount, selectedProduct.currency)} et ${money(maxAmount, selectedProduct.currency)}.`);
      return;
    }

    const payload = {
      ...investmentForm,
      amount_invested: investmentForm.subscription_amount,
      agent_recovery_id: editingInvestment?.agent_recovery_id || null,
    };

    await withBusy(editingInvestment ? "Modification de l'investissement..." : "Creation de la demande...", async () => {
      if (editingInvestment) {
        await updateInvestment(editingInvestment.id, payload);
      } else {
        await createInvestment(payload);
      }

      setInvestmentForm(investmentInitialForm);
      setEditingInvestment(null);
      setModal(null);
      setNotice(editingInvestment ? "Demande d'investissement modifiee." : "Demande d'investissement enregistree.");
      await load();
    });
  };

  const openPaymentForm = (investment = null) => {
    setPaymentForm({
      ...paymentInitialForm,
      investment_id: investment?.id || "",
      amount_paid: investment?.expected_contribution_amount || investment?.amount_to_pay || "",
    });
    setModal("payment");
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    const selected = recoveryOptions.find((item) => String(item.id) === String(paymentForm.investment_id));
    const label = selected ? `${selected.client_name} - ${money(paymentForm.amount_paid, safeCurrency(selected))}` : money(paymentForm.amount_paid, "CDF");
    if (!window.confirm(`Confirmer le recouvrement de ${label} ?`)) return;
    await withBusy("Enregistrement du paiement...", async () => {
      await createRecovery(paymentForm);
      setPaymentForm(paymentInitialForm);
      setModal(null);
      setNotice("Paiement terrain enregistre.");
      await load();
    });
  };

  const closeRecoveryDay = async () => {
    if (!canRecordRecovery) {
      setNotice("Votre profil ne peut pas cloturer un recouvrement.");
      return;
    }
    await withBusy("Cloture de la journee...", async () => {
      await createDailyClosure({ closure_date: new Date().toISOString().slice(0, 10) });
      setNotice("Journee cloturee. Rapport soumis au manager.");
      await load();
    });
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setNotice("La confirmation du nouveau mot de passe ne correspond pas.");
      return;
    }
    await withBusy("Modification du mot de passe...", async () => {
      await changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm(passwordInitialForm);
      setNotice("Mot de passe modifie.");
    });
  };

  const openClientFiche = async (client) => {
    setDetail({ type: "client", loading: true, title: client.full_name });
    try {
      const fiche = await fetchClientFiche(client.id);
      setDetail({ type: "client", loading: false, fiche });
    } catch (err) {
      setDetail(null);
      setNotice("Impossible de charger la fiche client.");
    }
  };

  const openInvestmentDetail = async (investment) => {
    setDetail({ type: "investment", loading: true, investment });
    try {
      const schedule = await fetchInvestmentSchedule(investment.id).catch(() => []);
      setDetail({ type: "investment", loading: false, investment, schedule });
    } catch (err) {
      setDetail({ type: "investment", loading: false, investment, schedule: [] });
    }
  };

  const openClosureDetail = async (closure) => {
    setDetail({ type: "closure", loading: true, closure });
    try {
      const report = await fetchDailyClosureDetails(closure.id);
      setDetail({ type: "closure", loading: false, report });
    } catch (err) {
      setDetail(null);
      setNotice("Impossible de charger le rapport journalier.");
    }
  };

  const markOneNotification = async (item) => {
    if (!item.read_at) {
      await markNotificationRead(item.id).catch(() => null);
      setNotifications((current) => current.map((row) => row.id === item.id ? { ...row, read_at: new Date().toISOString() } : row));
    }
  };

  const readAllNotifications = async () => {
    await markAllNotificationsRead();
    setNotifications((current) => current.map((row) => ({ ...row, read_at: row.read_at || new Date().toISOString() })));
  };

  const switchToDesktop = () => {
    localStorage.setItem(displayModeKey(user), "desktop");
    window.location.assign("/dashboard");
  };

  const signOut = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const renderHero = () => {
    const titleMap = {
      home: ["Bonjour, " + (user?.full_name || "Agent"), "Voici un apercu de votre portefeuille."],
      clients: ["Clients", "Creation et suivi de vos clients"],
      investments: ["Investissements", "Demandes et evolution des souscriptions"],
      recovery: ["Recouvrement", "Suivi des dossiers de recouvrement"],
      notifications: ["Notifications", "Alertes et operations recentes"],
      account: ["Compte", "Profil, securite et session"],
    };
    const [title, subtitle] = titleMap[tab] || titleMap.home;

    if (tab !== "home") {
      return (
        <section className="mobile-appbar">
          <div className="mobile-statusbar compact">
            <strong>9:41</strong>
            <span><SignalCellularAlt /><Wifi /><BatteryFull /></span>
          </div>
          <div className="mobile-appbar__line">
            <button type="button" onClick={() => setTab("home")} aria-label="Retour accueil"><Home /></button>
            <div>
              <h1>{title}</h1>
              <p>{subtitle}</p>
            </div>
            <button type="button" className="mobile-appbar__bell" onClick={() => setTab("notifications")} aria-label="Notifications">
              <NotificationsNone />
              {unreadCount > 0 && <span>{Math.min(unreadCount, 99)}</span>}
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className="mobile-hero">
        <div className="mobile-statusbar">
          <strong>9:41</strong>
          <span><SignalCellularAlt /><Wifi /><BatteryFull /></span>
        </div>
        <div className="mobile-hero__top">
          <div className="mobile-brand">
            <span>
              <img src="/OWSUR.png" className="sidebar-brand__img" alt="Client" />
            </span>
            <div>
              <strong>OWSUR-MATADI</strong>
              <small>CAPITAL INVESTMENT</small>
            </div>
          </div>
          <button type="button" className="mobile-bell" aria-label="Notifications" onClick={() => setTab("notifications")}>
            <NotificationsNone />
            {unreadCount > 0 && <span>{Math.min(unreadCount, 99)}</span>}
          </button>
        </div>
        <div className="mobile-welcome">
          <div>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </div>
      </section>
    );
  };

  const renderHome = () => (
    <>
      <section className="mobile-kpi-grid">
        <article className="mobile-kpi danger">
          <WarningAmber />
          <div><span>Incidents ouverts</span><strong>{number(scopedIncidents.length)}</strong><small>{number(visibleSummary.risk?.late_clients)} client(s) en retard</small></div>
        </article>
        <article className="mobile-kpi warning">
          <Shield />
          <div><span>Demandes</span><strong>{number(visibleSummary.investments?.investments_pending)}</strong><small>en attente</small></div>
        </article>
        <article className="mobile-kpi success">
          <Lock />
          <div><span>Recouvrements</span><strong>{currencyStack(visibleSummary.recoveries?.by_currency, "recovered_total")}</strong><small>mois courant</small></div>
        </article>
        <article className="mobile-kpi info">
          <Storage />
          <div><span>Clients</span><strong>{number(visibleSummary.portfolio?.total_clients)}</strong><small>{number(visibleSummary.portfolio?.new_clients)} nouveaux</small></div>
        </article>
      </section>

      <section className="mobile-card-grid">
        <article className="mobile-analytics-card">
          <h2>Portefeuille</h2>
          <div className="mobile-donut" style={{ "--value": `${Math.min(Number(visibleSummary.portfolio?.total_clients || 0) * 12, 100)}%` }}>
            <strong>{number(visibleSummary.portfolio?.total_clients)}</strong>
            <span>Total</span>
          </div>
          <ul>
            <li><span className="dot danger" /> Incidents <strong>{number(scopedIncidents.length)}</strong></li>
            <li><span className="dot warning" /> En demande <strong>{number(visibleSummary.investments?.investments_pending)}</strong></li>
            <li><span className="dot success" /> Clients <strong>{number(visibleSummary.portfolio?.total_clients)}</strong></li>
          </ul>
        </article>
        <article className="mobile-analytics-card">
          <h2>Recouvrement</h2>
          <div className="mobile-line-chart">
            {scopedRecoveries.slice(0, 7).map((item, index) => (
              <span key={`${item.id}-${index}`} style={{ height: `${28 + (index % 4) * 16}px` }} />
            ))}
            {!scopedRecoveries.length && [0, 1, 2, 3, 4, 5, 6].map((item) => <span key={item} style={{ height: `${24 + item * 7}px` }} />)}
          </div>
          <a href="#recouvrement" onClick={(event) => { event.preventDefault(); setTab("recovery"); }}>Voir le rapport <ChevronRight /></a>
        </article>
      </section>

      <section className="mobile-security-card">
        <GppGoodIcon />
        <div>
          <span>Posture du portefeuille</span>
          <strong>Active</strong>
          <p>Vos donnees sont limitees a votre portefeuille.</p>
        </div>
        <ul>
          <li><CheckCircle /> Clients affectes <strong>{number(scopedClients.length)}</strong></li>
          <li><CheckCircle /> Investissements <strong>{number(scopedInvestments.length)}</strong></li>
          <li><CheckCircle /> Paiements <strong>{number(scopedRecoveries.length)}</strong></li>
        </ul>
      </section>

      <section className="mobile-list-card">
        <header><h2>Activites recentes</h2><button type="button" onClick={() => setTab("notifications")}>Voir toutes <ChevronRight /></button></header>
        {(notifications.length ? notifications.slice(0, 4) : scopedRecoveries.slice(0, 4)).map((item, index) => (
          <article key={`${item.id}-${index}`}>
            <time>{item.created_at ? new Date(item.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : dateOnly(item.paid_at)}</time>
            <span className={`mobile-chip ${statusClass(item.priority || item.status)}`}>{item.priority || item.status || "Info"}</span>
            <strong>{item.title || item.client_name || "Mouvement"}</strong>
            <small>{item.message || (item.amount_paid ? money(item.amount_paid, item.currency) : item.investment_reference)}</small>
          </article>
        ))}
      </section>
    </>
  );

  const renderToolbar = ({ title, count, onAdd, addLabel = "Ajouter", filterLabel = "Tous" }) => (
    <section className="mobile-toolbar-card">
      <label className="mobile-search">
        <Search />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Recherche avancee : client, dossier, telephone..." />
      </label>
      <div className="mobile-actions-row">
        {onAdd && <button type="button" className="primary" onClick={onAdd}><Add /> {addLabel}</button>}
        <button type="button"><FilterList /> Filtrer / Trier</button>
      </div>
      <div className="mobile-result-row">
        <span><Folder /> {number(count)} {title}</span>
        <button type="button">Affichage : {filterLabel}</button>
      </div>
    </section>
  );

  const renderPagination = (key, pagination) => (
    <div className="mobile-pagination">
      <button type="button" disabled={pagination.page <= 1} onClick={() => setActivePage(key, pagination.page - 1)}>Precedent</button>
      <strong>{pagination.page}</strong>
      <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setActivePage(key, pagination.page + 1)}>Suivant <ChevronRight /></button>
    </div>
  );

  const renderClients = () => (
    <section className="mobile-module-page">
      {renderToolbar({ title: "clients trouves", count: filteredClients.length, onAdd: () => openClientForm() })}
      <div className="mobile-case-list">
        {clientPage.rows.map((client) => {
          const investment = latestInvestmentByClient.get(Number(client.id));
          return (
            <article className="mobile-client-card" key={client.id} onClick={() => openClientFiche(client)}>
              <span className="case-icon info"><Person /></span>
              <div className="case-main">
                <strong>{client.full_name || `${client.last_name || ""} ${client.first_name || ""}`.trim()}</strong>
                <span>{client.phone || client.client_number || client.generated_client_id}</span>
                <small>{investment?.product_name || "Aucun investissement en cours"}</small>
              </div>
              <div className="case-side">
                <strong>{investment ? money(investment.amount_invested || investment.subscription_amount, safeCurrency(investment)) : "-"}</strong>
                <small>{investment ? safeCurrency(investment) : ""}</small>
              </div>
              <button className="mobile-card-action" type="button" onClick={(event) => { event.stopPropagation(); openClientForm(client); }}>
                <Edit />
              </button>
              <ChevronRight />
            </article>
          );
        })}
      </div>
      {renderPagination("clients", clientPage)}
    </section>
  );

  const renderInvestments = () => (
    <section className="mobile-module-page">
      {renderToolbar({
        title: "investissements trouves",
        count: filteredInvestments.length,
        onAdd: canCreateOrEditInvestment ? () => openInvestmentForm() : null,
      })}
      <div className="mobile-case-list">
        {investmentPage.rows.map((item) => {
          const cls = statusClass(item.status);
          return (
            <article className="mobile-case-card" key={item.id} onClick={() => openInvestmentDetail(item)}>
              <span className={`case-icon ${cls}`}>{cls === "success" ? <CheckCircle /> : cls === "warning" ? <CalendarMonth /> : <ErrorOutline />}</span>
              <div className="case-main">
                <strong>{item.collection_card_id || item.reference}</strong>
                <span>{item.client_name}</span>
                <small>{item.product_name}</small>
                <em><CalendarMonth /> Debut : {dateOnly(item.repayment_start_date || item.created_at)}</em>
              </div>
              <div className="case-side">
                <strong>{money(item.amount_invested || item.subscription_amount, safeCurrency(item))}</strong>
                <small className={`mobile-chip ${cls}`}>{item.status}</small>
              </div>
              {canEditInvestment(item) && (
                <button className="mobile-card-action" type="button" onClick={(event) => { event.stopPropagation(); openInvestmentForm(item); }}>
                  <Edit />
                </button>
              )}
              <ChevronRight />
            </article>
          );
        })}
      </div>
      {renderPagination("investments", investmentPage)}
    </section>
  );

  const renderRecovery = () => (
    <section className="mobile-module-page">
      {renderToolbar({ title: "dossiers trouves", count: recoveryDossiers.length, onAdd: canRecordRecovery ? () => openPaymentForm() : null, addLabel: "Encaisser" })}
      {canRecordRecovery && (
        <div className="mobile-recovery-actions">
          <button type="button" onClick={closeRecoveryDay}><AssignmentTurnedIn /> Cloturer la journee</button>
        </div>
      )}
      <div className="mobile-case-list">
        {recoveryPage.rows.map((item) => {
          const cls = statusClass(item.status);
          return (
            <article className="mobile-case-card" key={item.id} onClick={() => openInvestmentDetail(item)}>
              <span className={`case-icon ${cls}`}>{cls === "success" ? <CheckCircle /> : cls === "warning" ? <CalendarMonth /> : <ErrorOutline />}</span>
              <div className="case-main">
                <strong>{item.collection_card_id || item.reference}</strong>
                <span>{item.client_name}</span>
                <small>{item.product_name}</small>
                <em><CalendarMonth /> Echeance : {dateOnly(item.repayment_end_date)}</em>
              </div>
              <div className="case-side">
                <strong>{money(item.remaining_amount, safeCurrency(item))}</strong>
                <small className={`mobile-chip ${cls}`}>Cot. {money(item.expected_contribution_amount || item.amount_to_pay, safeCurrency(item))}</small>
              </div>
              <ChevronRight />
            </article>
          );
        })}
      </div>
      {renderPagination("recovery", recoveryPage)}
      <section className="mobile-list-card mobile-closure-card">
        <header><h2>Rapports soumis</h2><button type="button">{closures.length} rapport(s)</button></header>
        {closures.length ? closures.slice(0, 4).map((closure) => (
          <article key={closure.id} onClick={() => openClosureDetail(closure)}>
            <time>{dateOnly(closure.closure_date)}</time>
            <span className={`mobile-chip ${statusClass(closure.status)}`}>{closure.status}</span>
            <strong>{closure.agent_name || "Rapport journalier"}</strong>
            <small>{money(closure.total_collected_cdf, "CDF")} / {money(closure.total_collected_usd, "USD")}</small>
          </article>
        )) : <p className="mobile-empty-text">Aucun rapport journalier sur la periode.</p>}
      </section>
    </section>
  );

  const renderNotifications = () => (
    <section className="mobile-module-page">
      <div className="mobile-section-head">
        <div>
          <h2>Notifications</h2>
          <p>{number(unreadCount)} non lue(s)</p>
        </div>
        <button type="button" onClick={readAllNotifications}>Tout lire</button>
      </div>
      <div className="mobile-notification-list">
        {notificationPage.rows.map((item) => (
          <article key={item.id} className={!item.read_at ? "is-unread" : ""} onClick={() => markOneNotification(item)}>
            <span className={`case-icon ${statusClass(item.priority)}`}><NotificationsNone /></span>
            <div>
              <strong>{item.title}</strong>
              <p>{item.message}</p>
              <time>Temps : {compactDateTime(item.created_at)}</time>
            </div>
          </article>
        ))}
      </div>
      {renderPagination("notifications", notificationPage)}
    </section>
  );

  const renderAccount = () => (
    <section className="mobile-account-page">
      <article className="mobile-profile-card">
        <AccountCircle />
        <div>
          <strong>{user?.full_name || "Utilisateur"}</strong>
          <span>{user?.role_label || role}</span>
          <small>{user?.email || user?.phone || "-"}</small>
        </div>
      </article>
      <form className="mobile-form-card" onSubmit={submitPassword}>
        <h2>Modifier le mot de passe</h2>
        <div className="mobile-form">
          <label>Mot de passe actuel<input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required /></label>
          <label>Nouveau mot de passe<input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required /></label>
          <label>Confirmer<input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required /></label>
          <button className="mobile-primary" type="submit"><Key /> Enregistrer</button>
        </div>
      </form>
      <button className="mobile-secondary" type="button" onClick={switchToDesktop}>Basculer en format PC</button>
      <button className="mobile-danger-button" type="button" onClick={signOut}><Logout /> Se deconnecter</button>
    </section>
  );

  const renderClientModal = () => (
    <form className="mobile-form" onSubmit={submitClient}>
      <div className="mobile-two-cols">
        <label>Nom<input value={clientForm.last_name} onChange={(e) => setClientForm({ ...clientForm, last_name: e.target.value })} required /></label>
        <label>Prenom<input value={clientForm.first_name} onChange={(e) => setClientForm({ ...clientForm, first_name: e.target.value })} required /></label>
      </div>
      <div className="mobile-two-cols">
        <label>Sexe<select value={clientForm.sex} onChange={(e) => setClientForm({ ...clientForm, sex: e.target.value })}><option value="M">Masculin</option><option value="F">Feminin</option></select></label>
        <label>Telephone<input value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} /></label>
      </div>
      <label>Adresse complete<input value={clientForm.address} onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })} /></label>
      <label>Commune<input value={clientForm.commune} onChange={(e) => setClientForm({ ...clientForm, commune: e.target.value })} /></label>
      <div className="mobile-activity-picker">
        {userHasFullAccess && (
          <div className="mobile-inline-create">
            <input value={newActivity} onChange={(e) => setNewActivity(e.target.value)} placeholder="Nouvelle activite" />
            <button type="button" onClick={addActivity}><Add /></button>
          </div>
        )}
        <div className="mobile-check-grid">
          {activityCatalog.map((activity) => (
            <label key={activity.id}>
              <input type="checkbox" checked={clientForm.activity_ids.includes(String(activity.id))} onChange={() => toggleActivity(activity.id)} />
              <span>{activity.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="mobile-two-cols">
        <label>Revenu journalier<input type="number" value={clientForm.estimated_daily_revenue} onChange={(e) => setClientForm({ ...clientForm, estimated_daily_revenue: e.target.value })} /></label>
        <label>Revenu mensuel auto<input type="number" value={monthlyClientRevenue} readOnly /></label>
      </div>
      <div className="mobile-two-cols">
        <label>Charges mensuelles<input type="number" value={clientForm.estimated_monthly_charges} onChange={(e) => setClientForm({ ...clientForm, estimated_monthly_charges: e.target.value })} /></label>
        <label>Fonds de roulement<input type="number" value={clientForm.existing_working_capital} onChange={(e) => setClientForm({ ...clientForm, existing_working_capital: e.target.value })} /></label>
      </div>
      <label>Score de risque /10<input type="number" min="0" max="10" value={clientForm.risk_score} onChange={(e) => setClientForm({ ...clientForm, risk_score: e.target.value })} /></label>
      <div className="mobile-file-grid">
        <label>Photo client<input type="file" accept="image/*" onChange={(e) => setClientForm({ ...clientForm, photo_client: e.target.files?.[0] || null })} /></label>
        <label>Piece d'identite<input type="file" accept="image/*" onChange={(e) => setClientForm({ ...clientForm, identity_card: e.target.files?.[0] || null })} /></label>
        <label>Photo activite<input type="file" accept="image/*" onChange={(e) => setClientForm({ ...clientForm, business_photo: e.target.files?.[0] || null })} /></label>
      </div>
      <button className="mobile-primary" type="submit"><Save /> {editingClient ? "Modifier le client" : "Enregistrer le client"}</button>
    </form>
  );

  const renderInvestmentModal = () => (
    <form className="mobile-form" onSubmit={submitInvestment}>
      <label>Client<select value={investmentForm.client_id} onChange={(e) => setInvestmentForm({ ...investmentForm, client_id: e.target.value })} required><option value="">Selectionner</option>{scopedClients.map((client) => <option key={client.id} value={client.id}>{client.full_name}</option>)}</select></label>
      <label>Produit<select value={investmentForm.product_id} onChange={(e) => setInvestmentForm({ ...investmentForm, product_id: e.target.value })} required><option value="">Selectionner</option>{products.map((product) => <option key={product.id} value={product.id}>{product.name} - {product.currency}</option>)}</select></label>
      <label>Montant<input type="number" value={investmentForm.subscription_amount} onChange={(e) => setInvestmentForm({ ...investmentForm, subscription_amount: e.target.value })} required /></label>
      {selectedProduct && (
        <div className="mobile-calculation-box">
          <span>Commission : {money(commission, selectedProduct.currency)}</span>
          <span>Montant net : {money(net, selectedProduct.currency)}</span>
          <span>Total a rembourser : {money(preview.amountToRecover, selectedProduct.currency)}</span>
          <span>Cotisation prevue : {money(expectedContribution, selectedProduct.currency)}</span>
          <span>Plafond : {money(minAmount, selectedProduct.currency)} - {money(maxAmount, selectedProduct.currency)}</span>
          {isBootstrapping(selectedProduct) && <span>Commission mensuelle : {money(preview.monthlyCommission, selectedProduct.currency)} x {contributionInstallments(selectedProduct)} mois</span>}
        </div>
      )}
      <label>Debut remboursement<input type="date" value={investmentForm.repayment_start_date} onChange={(e) => setInvestmentForm({ ...investmentForm, repayment_start_date: e.target.value })} /></label>
      <label>Notes analyse<textarea value={investmentForm.analysis_notes} onChange={(e) => setInvestmentForm({ ...investmentForm, analysis_notes: e.target.value })} /></label>
      <button className="mobile-primary" type="submit"><Save /> {editingInvestment ? "Modifier" : "Creer la demande"}</button>
    </form>
  );

  const renderPaymentModal = () => (
    <form className="mobile-form" onSubmit={submitPayment}>
      <label>Dossier<select value={paymentForm.investment_id} onChange={(e) => {
        const investment = recoveryOptions.find((item) => String(item.id) === String(e.target.value));
        setPaymentForm({
          ...paymentForm,
          investment_id: e.target.value,
          amount_paid: investment?.expected_contribution_amount || investment?.amount_to_pay || paymentForm.amount_paid,
        });
      }} required><option value="">Selectionner</option>{recoveryOptions.map((investment) => <option value={investment.id} key={investment.id}>{investment.client_name} - cotisation {money(investment.expected_contribution_amount || investment.amount_to_pay, safeCurrency(investment))}</option>)}</select></label>
      <label>Montant<input type="number" value={paymentForm.amount_paid} onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })} required /></label>
      <div className="mobile-location">
        <span>{paymentForm.latitude ? `${paymentForm.latitude}, ${paymentForm.longitude}` : "Position non renseignee"}</span>
        <button type="button" onClick={locate}><LocationOn /></button>
      </div>
      <label>Observation<textarea value={paymentForm.observation} onChange={(e) => setPaymentForm({ ...paymentForm, observation: e.target.value })} /></label>
      <button className="mobile-primary" type="submit"><Save /> Valider</button>
    </form>
  );

  const renderModal = () => {
    if (!modal) return null;
    const titles = {
      client: editingClient ? "Modifier client" : "Nouveau client",
      investment: editingInvestment ? "Modifier investissement" : "Nouvel investissement",
      payment: "Encaisser une cotisation",
    };
    return (
      <div className="mobile-modal-backdrop">
        <section className="mobile-modal">
          <header>
            <h2>{titles[modal]}</h2>
            <button type="button" onClick={() => setModal(null)}><Close /></button>
          </header>
          {modal === "client" && renderClientModal()}
          {modal === "investment" && renderInvestmentModal()}
          {modal === "payment" && renderPaymentModal()}
        </section>
      </div>
    );
  };

  const renderDetail = () => {
    if (!detail) return null;
    const close = () => setDetail(null);
    const client = detail.fiche?.client;
    const investment = detail.investment;
    const schedule = detail.schedule || [];

    return (
      <div className="mobile-modal-backdrop">
        <section className="mobile-modal detail">
          <header>
            <h2>{detail.type === "client" ? "Fiche client" : detail.type === "closure" ? "Rapport journalier" : "Fiche dossier"}</h2>
            <button type="button" onClick={close}><Close /></button>
          </header>
          {detail.loading && <p className="mobile-loading">Chargement...</p>}
          {!detail.loading && detail.type === "client" && client && (
            <div className="mobile-detail-stack">
              <article>
                <h3>{client.full_name}</h3>
                <p>{client.phone || "-"} - {client.commune || client.address || "-"}</p>
                <p>ID : {client.generated_client_id || client.client_number || client.id}</p>
              </article>
              <article>
                <h3>Activites</h3>
                {(detail.fiche.activities || []).map((activity) => <span className="mobile-tag" key={activity.id || activity.activity_name}>{activity.activity_name || activity.name}</span>)}
              </article>
              <article>
                <h3>Investissements</h3>
                {(detail.fiche.investments || []).map((item) => (
                  <p key={item.id}>{item.product_name} - {money(item.amount_invested, safeCurrency(item))} - {item.status}</p>
                ))}
              </article>
              <article>
                <h3>Recouvrements</h3>
                {(detail.fiche.recoveries || []).slice(0, 8).map((item) => (
                  <p key={item.id}>{dateOnly(item.paid_at)} - {money(item.amount_paid, safeCurrency(item))}</p>
                ))}
              </article>
              <article>
                <h3>Historique</h3>
                {(detail.fiche.auditLogs || []).slice(0, 8).map((item) => (
                  <p key={item.id}>{compactDateTime(item.created_at)} - {item.action}</p>
                ))}
              </article>
            </div>
          )}
          {!detail.loading && detail.type === "investment" && investment && (
            <div className="mobile-detail-stack">
              <article>
                <h3>{investment.collection_card_id || investment.reference}</h3>
                <p>{investment.client_name} - {investment.product_name}</p>
                <p>Status : {investment.status}</p>
              </article>
              <article className="mobile-progress-box">
                <span>A recouvrer : {money(investment.amount_to_recover, safeCurrency(investment))}</span>
                <span>Cotisation prevue : {money(investment.expected_contribution_amount || investment.amount_to_pay, safeCurrency(investment))}</span>
                <span>Recouvre : {money(investment.amount_recovered, safeCurrency(investment))}</span>
                <span>Reste : {money(investment.remaining_amount, safeCurrency(investment))}</span>
                <div><i style={{ width: `${Math.min(Number(investment.recovery_rate || 0), 100)}%` }} /></div>
              </article>
              <article>
                <h3>Echeancier</h3>
                {schedule.slice(0, 10).map((item) => (
                  <p key={item.id || item.installment_number}>
                    #{item.installment_number} - {dateOnly(item.due_date)} - {money(item.expected_amount, safeCurrency(investment))} - {item.status}
                    {String(item.status || "").toUpperCase() === "PAID" && item.payment_reference && (
                      <small className="mobile-schedule-ref">Ref paiement : {item.payment_reference}</small>
                    )}
                  </p>
                ))}
              </article>
              {canRecordRecovery && <button className="mobile-primary" type="button" onClick={() => { close(); openPaymentForm(investment); }}><Payments /> Encaisser sur ce dossier</button>}
            </div>
          )}
          {!detail.loading && detail.type === "closure" && detail.report?.closure && (
            <div className="mobile-detail-stack">
              <article>
                <h3>{detail.report.closure.agent_name}</h3>
                <p>{dateOnly(detail.report.closure.closure_date)} - {detail.report.closure.status}</p>
                <p>{detail.report.closure.validated_by_name ? `Valide par ${detail.report.closure.validated_by_name}` : "En attente manager"}</p>
              </article>
              <article className="mobile-progress-box">
                {["CDF", "USD"].map((currency) => (
                  <span key={currency}>{currency} : verse {money(detail.report.totals?.[currency]?.collected, currency)} / reste {money(detail.report.totals?.[currency]?.remaining, currency)}</span>
                ))}
              </article>
              <article>
                <h3>Clients recouvres</h3>
                {(detail.report.rows || []).map((row) => (
                  <p key={row.id}>{row.client_name} - {row.product_name} - {money(row.amount_paid, row.currency)}</p>
                ))}
              </article>
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <main className={`mobile-workspace ${tab === "home" ? "is-home" : "is-inner"}`}>
      {renderHero()}
      {notice && <p className="mobile-notice">{notice}</p>}
      <div className="mobile-content">
        {tab === "home" && renderHome()}
        {tab === "clients" && renderClients()}
        {tab === "investments" && renderInvestments()}
        {tab === "recovery" && renderRecovery()}
        {tab === "notifications" && renderNotifications()}
        {tab === "account" && renderAccount()}
      </div>
      <nav className="mobile-bottom-nav" style={{ "--nav-count": navItems.length }}>
        {navItems.map((item) => (
          <button type="button" key={item.key} className={tab === item.key ? "active" : ""} onClick={() => setTab(item.key)}>
            <span className="mobile-nav-icon">{item.icon}{item.badge > 0 && <i>{Math.min(item.badge, 99)}</i>}</span>
            {item.label}
          </button>
        ))}
      </nav>
      {renderModal()}
      {renderDetail()}
      <LoadingOverlay show={busy} label={busyLabel} />
    </main>
  );
}

function GppGoodIcon() {
  return (
    <span className="mobile-shield-big">
      <Shield />
      <CheckCircle />
    </span>
  );
}

export default MobileWorkspace;
